import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Edit3 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
    { key: "todo", label: "To do" },
    { key: "in_progress", label: "In progress" },
    { key: "done", label: "Done" },
];
const PRIORITIES = [
    { key: "low", label: "Low", color: "text-muted-foreground" },
    { key: "medium", label: "Medium", color: "text-accent" },
    { key: "high", label: "High", color: "text-destructive" },
];

export default function Assignments() {
    const [items, setItems] = useState([]);
    const [courses, setCourses] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [params, setParams] = useSearchParams();
    const [view, setView] = useState("list");
    const [filterCourse, setFilterCourse] = useState("all");

    const empty = { title: "", description: "", course_id: null, priority: "medium", status: "todo", due_date: "", estimated_minutes: 60 };
    const [form, setForm] = useState(empty);

    const load = async () => {
        const [a, c] = await Promise.all([api.get("/assignments"), api.get("/courses")]);
        setItems(a.data); setCourses(c.data);
    };
    useEffect(() => { load(); }, []);
    useEffect(() => {
        if (params.get("new") === "1") { setOpen(true); setEditing(null); setForm(empty); params.delete("new"); setParams(params); }
        const cid = params.get("course"); if (cid) setFilterCourse(cid);
    }, [params]);

    const filtered = useMemo(() => filterCourse === "all" ? items : items.filter(a => a.course_id === filterCourse), [items, filterCourse]);

    const sortedFiltered = useMemo(() => filtered.slice().sort(
        (a, b) => (a.status === "done") - (b.status === "done")
            || (new Date(a.due_date || 8640000000000) - new Date(b.due_date || 8640000000000))
    ), [filtered]);

    const save = async () => {
        try {
            const payload = { ...form, due_date: form.due_date || null, course_id: form.course_id || null };
            if (editing) await api.patch(`/assignments/${editing.id}`, payload);
            else await api.post("/assignments", payload);
            toast.success(editing ? "Updated" : "Created");
            setOpen(false); setEditing(null); setForm(empty); load();
        } catch { toast.error("Save failed"); }
    };

    const remove = async (a) => { if (!window.confirm(`Delete "${a.title}"?`)) return; await api.delete(`/assignments/${a.id}`); load(); };

    const toggleDone = async (a) => {
        const next = a.status === "done" ? "todo" : "done";
        await api.patch(`/assignments/${a.id}`, { status: next }); load();
    };

    const setStatus = async (a, s) => { await api.patch(`/assignments/${a.id}`, { status: s }); load(); };

    const openEdit = (a) => {
        setEditing(a);
        setForm({ title: a.title, description: a.description || "", course_id: a.course_id, priority: a.priority, status: a.status, due_date: (a.due_date || "").slice(0, 10), estimated_minutes: a.estimated_minutes || 60 });
        setOpen(true);
    };

    const courseColor = (id) => courses.find(c => c.id === id)?.color || "hsl(var(--muted))";
    const courseName = (id) => courses.find(c => c.id === id)?.name || "No course";

    return (
        <div className="space-y-8" data-testid="assignments-page">
            <header className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="mono-label">Assignments</div>
                    <h1 className="font-display text-4xl font-medium tracking-tight mt-2">Get it done</h1>
                    <p className="text-muted-foreground mt-1">{items.filter(a => a.status !== "done").length} open · {items.filter(a => a.status === "done").length} completed</p>
                </div>
                <div className="flex items-center gap-3">
                    <Select value={filterCourse} onValueChange={setFilterCourse}>
                        <SelectTrigger className="w-[180px]" data-testid="filter-course"><SelectValue placeholder="Course" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All courses</SelectItem>
                            {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="new-assignment-btn"><Plus className="w-4 h-4 mr-1.5" /> New</Button>
                </div>
            </header>

            <Tabs value={view} onValueChange={setView}>
                <TabsList data-testid="assignment-view-tabs">
                    <TabsTrigger value="list" data-testid="tab-list">List</TabsTrigger>
                    <TabsTrigger value="board" data-testid="tab-board">Board</TabsTrigger>
                </TabsList>

                <TabsContent value="list" className="mt-6">
                    {filtered.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-border p-16 text-center">
                            <div className="font-display text-xl mb-2">Nothing here yet</div>
                            <p className="text-sm text-muted-foreground mb-4">Create your first assignment.</p>
                            <Button onClick={() => setOpen(true)}>New assignment</Button>
                        </div>
                    ) : (
                        <ul className="bg-card rounded-2xl border border-border/60 divide-y divide-border/50 overflow-hidden">
                            {sortedFiltered.map(a => (
                                <li key={a.id} className="p-4 flex items-center gap-4 hover:bg-muted/30 transition" data-testid={`assignment-${a.id}`}>
                                    <Checkbox checked={a.status === "done"} onCheckedChange={() => toggleDone(a)} data-testid={`check-${a.id}`} />
                                    <span className="w-1 h-8 rounded-full" style={{ background: courseColor(a.course_id) }} />
                                    <div className="flex-1 min-w-0">
                                        <div className={`text-sm font-medium truncate ${a.status === "done" ? "line-through text-muted-foreground" : ""}`}>{a.title}</div>
                                        <div className="text-xs text-muted-foreground truncate">{courseName(a.course_id)} · {a.estimated_minutes || 0}m est</div>
                                    </div>
                                    <span className={`text-[11px] uppercase tracking-wider font-mono ${PRIORITIES.find(p => p.key === a.priority)?.color}`}>{a.priority}</span>
                                    <span className="text-xs font-mono text-muted-foreground min-w-[70px] text-right">{a.due_date ? new Date(a.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}</span>
                                    <button className="p-1.5 rounded-md hover:bg-muted" onClick={() => openEdit(a)}><Edit3 className="w-3.5 h-3.5" /></button>
                                    <button className="p-1.5 rounded-md hover:bg-muted text-destructive" onClick={() => remove(a)}><Trash2 className="w-3.5 h-3.5" /></button>
                                </li>
                            ))}
                        </ul>
                    )}
                </TabsContent>

                <TabsContent value="board" className="mt-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {STATUSES.map(s => (
                            <div key={s.key} className="bg-card/60 rounded-2xl border border-border/60 p-4" data-testid={`col-${s.key}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="mono-label">{s.label}</div>
                                    <span className="text-xs text-muted-foreground">{filtered.filter(a => a.status === s.key).length}</span>
                                </div>
                                <div className="space-y-3">
                                    {filtered.filter(a => a.status === s.key).map(a => (
                                        <div key={a.id} className="card-hover bg-background rounded-xl border border-border/60 p-3.5 cursor-pointer" onClick={() => openEdit(a)}>
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="w-2 h-2 rounded-full" style={{ background: courseColor(a.course_id) }} />
                                                <span className={`text-[10px] uppercase tracking-wider font-mono ${PRIORITIES.find(p => p.key === a.priority)?.color}`}>{a.priority}</span>
                                            </div>
                                            <div className="text-sm font-medium mb-1">{a.title}</div>
                                            <div className="text-xs text-muted-foreground truncate">{courseName(a.course_id)}</div>
                                            <div className="flex gap-1 mt-3">
                                                {STATUSES.filter(x => x.key !== s.key).map(x => (
                                                    <button key={x.key} onClick={(e) => { e.stopPropagation(); setStatus(a, x.key); }} className="text-[10px] px-2 py-0.5 rounded-full border border-border hover:bg-muted transition">→ {x.label}</button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="assignment-dialog">
                    <DialogHeader><DialogTitle className="font-display">{editing ? "Edit assignment" : "New assignment"}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><Label className="mono-label text-xs">Title</Label><Input data-testid="assign-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
                        <div><Label className="mono-label text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={3} /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="mono-label text-xs">Course</Label>
                                <Select value={form.course_id || "none"} onValueChange={v => setForm({ ...form, course_id: v === "none" ? null : v })}>
                                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No course</SelectItem>
                                        {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="mono-label text-xs">Priority</Label>
                                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>{PRIORITIES.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="mono-label text-xs">Due date</Label><Input type="date" value={form.due_date || ""} onChange={e => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" /></div>
                            <div><Label className="mono-label text-xs">Est. minutes</Label><Input type="number" value={form.estimated_minutes} onChange={e => setForm({ ...form, estimated_minutes: parseInt(e.target.value || "0") })} className="mt-1.5" /></div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="save-assignment" disabled={!form.title.trim()}>{editing ? "Update" : "Create"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
